import React from 'react';

import { LayoutComposer, NamedLink } from '../../components';
import GemstoneSiteFooter from '../../components/GemstoneSiteFooter/GemstoneSiteFooter';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import StaticPage from '../PageBuilder/StaticPage';

import css from './TradeAccessPage.module.css';

/* Photography from the GemstoneX design (assets/). trade-cta-bg.jpg is the real
   file; trade-hero-stone.jpg + trade-verified-example.jpg are emerald stand-ins
   (the real files exceed the design tool's 256KB fetch limit) — copy them in
   from the design assets/ (same names → auto-swap). */
import heroStone from './images/trade-hero-stone.jpg';
import verifiedExample from './images/trade-verified-example.jpg';
import ctaBg from './images/trade-cta-bg.jpg';

const META = {
  title: 'Trade Access — GemstoneX',
  description:
    'Your global sourcing extension. Access certified emeralds and coloured stones through a verified, documented, escrow-settled trade process. Access is by application.',
  schema: {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    name: 'Trade Access — GemstoneX',
  },
};

/* ----------------------------- icons (ported from source) ---------------- */
const Arrow = props => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M4 10.5l3.5 3.5L16 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconGem = () => (
  <svg viewBox="0 0 32 32">
    <path d="M16 3 L28 10 L28 22 L16 29 L4 22 L4 10 Z" strokeWidth="1.6" />
    <path d="M4 10 L16 17 L28 10 M16 17 L16 29" strokeWidth="1.4" />
    <path d="M4 10 L16 3 M28 10 L16 3" strokeWidth="1.4" />
  </svg>
);
const IconDoc = () => (
  <svg viewBox="0 0 32 32">
    <path d="M9 4 L20 4 L25 9 L25 28 L9 28 Z" strokeWidth="1.6" />
    <path d="M20 4 L20 9 L25 9" strokeWidth="1.5" />
    <path d="M13 16 L21 16 M13 21 L21 21" strokeWidth="1.5" />
  </svg>
);
const IconPricing = () => (
  <svg viewBox="0 0 32 32">
    <path d="M17 4 L27 4 L27 14 L15 26 L5 16 Z" strokeWidth="1.6" />
    <circle cx="22" cy="9" r="2" strokeWidth="1.5" />
  </svg>
);
const IconLock = () => (
  <svg viewBox="0 0 32 32">
    <rect x="7" y="14" width="18" height="13" rx="2.5" strokeWidth="1.6" />
    <path d="M11 14 C11 9 13 6 16 6 C19 6 21 9 21 14" strokeWidth="1.6" />
    <circle cx="16" cy="20" r="2" fill="currentColor" stroke="none" />
  </svg>
);
const BadgeVerify = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12 2.5 2.5 4.5-5" />
  </svg>
);
const BadgeLab = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const PILLS = ['Independent lab certification', 'KYC-verified trade only', 'Escrow on regulated rails'];

const PROMISES = [
  { Icon: IconGem, h: 'Verified Origin', p: 'Provenance documented for every stone.' },
  { Icon: IconPricing, h: 'Trade Pricing', p: 'Wholesale access, single pieces to calibrated lots.' },
  { Icon: IconLock, h: 'White-Label Ready', p: 'Present verified stones under your own brand.' },
  {
    Icon: IconDoc,
    h: 'Full Documentation',
    p: 'Lab certificate, treatment and origin recorded for every stone.',
  },
];

const PERSONAS = [
  {
    h: 'Independent jewellers',
    p: 'Access distinctive certified stones without building sourcing infrastructure yourself.',
  },
  {
    h: 'Designers and manufacturers',
    p: 'Source to a spec with consistency, for bespoke commissions and calibrated repeat demand.',
  },
  {
    h: 'Dealers and trade buyers',
    p: 'Extend your range without managing the sourcing complexity directly.',
  },
  {
    h: 'Investors and private buyers',
    p: 'Acquire fully documented, independently certified stones with verified provenance, held to trade standards.',
  },
];

const YOU_MANAGE = ['Clients', 'Design intent', 'Product direction', 'Final sale'];
const GSX_MANAGES = ['Sourcing access', 'Quality review', 'Documentation', 'Imagery', 'Logistics', 'Settlement'];

const TradeAccessPage = () => {
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <StaticPage {...META}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="TradeAccessPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                <div className={css.jl}>
                  {/* Hero */}
                  <header className={css.hero}>
                    <div className={`${css.wrap} ${css.heroInner}`}>
                      <div className={css.heroGrid}>
                        <div className={css.heroCopy}>
                          <div className={css.eyebrow}>Wholesale &amp; Trade</div>
                          <div className={css.rule} />
                          <h1>
                            Your global <em>sourcing extension.</em>
                          </h1>
                          <p>
                            Stop searching. Start selling. Access certified emeralds and coloured
                            stones through a verified sourcing process built on documentation, quality
                            control and secure trade settlement.
                          </p>
                          <NamedLink name="SignupPage" className={`${css.btn} ${css.btnPrimary}`}>
                            Apply for Trade Access <Arrow />
                          </NamedLink>
                        </div>
                        <div className={css.heroCard}>
                          <div className={css.stone}>
                            <div className={css.stoneImg}>
                              <img src={heroStone} alt="A certified vivid green oval emerald" />
                              <span className={css.stoneVerify}>
                                <BadgeVerify /> Verified
                              </span>
                              <span className={css.stoneCert}>
                                <BadgeLab /> GRS
                              </span>
                            </div>
                            <div className={css.stoneBody}>
                              <p className={css.stoneTitle}>Colombian Emerald, Oval</p>
                              <div className={css.stoneTags}>
                                <span className={css.stoneTag}>3.45 ct</span>
                                <span className={css.stoneTag}>Colombia</span>
                              </div>
                              <dl className={css.stoneRows}>
                                <div className={css.stoneRow}>
                                  <dt>Color</dt>
                                  <dd>Vivid Green</dd>
                                </div>
                                <div className={css.stoneRow}>
                                  <dt>Cut</dt>
                                  <dd>Oval</dd>
                                </div>
                                <div className={css.stoneRow}>
                                  <dt>Treatment</dt>
                                  <dd>Insignificant (NCE)</dd>
                                </div>
                              </dl>
                              <hr className={css.stoneDiv} />
                              <div className={css.stonePrice}>
                                <span className={css.amt}>Price on Request</span>
                              </div>
                              <div className={css.stoneActions}>
                                <span className={css.stoneInq}>Inquire</span>
                                <span className={css.stoneDet}>View Details</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </header>

                  {/* Value pills */}
                  <div className={css.pills}>
                    <div className={css.pillsInner}>
                      {PILLS.map(t => (
                        <span className={css.pill} key={t}>
                          <Check /> {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Promise row */}
                  <section className={`${css.exp} ${css.section}`} id="why">
                    <div className={css.wrap}>
                      <div className={css.eyebrow}>Why GemstoneX</div>
                      <div className={css.rule} />
                      <h2>The infrastructure beneath your sourcing.</h2>
                      <div className={css.expGrid}>
                        {PROMISES.map(({ Icon, h, p }) => (
                          <div className={css.tile} key={h}>
                            <div className={css.tileIcon}>
                              <Icon />
                            </div>
                            <h3>{h}</h3>
                            <p>{p}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  {/* Personas */}
                  <section className={`${css.personas} ${css.section}`} id="for">
                    <div className={css.wrap}>
                      <div className={css.eyebrow}>Who it’s for</div>
                      <div className={css.rule} />
                      <h2>Built for the way trade buyers buy.</h2>
                      <div className={css.personasGrid}>
                        {PERSONAS.map(({ h, p }) => (
                          <div className={css.persona} key={h}>
                            <h3>{h}</h3>
                            <p>{p}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  {/* Workflow split */}
                  <section className={`${css.flow} ${css.section}`} id="workflow">
                    <div className={css.wrap}>
                      <div className={css.eyebrow}>The division of labour</div>
                      <div className={css.rule} />
                      <h2>How GemstoneX fits your workflow.</h2>
                      <div className={css.flowGrid}>
                        <div className={`${css.col} ${css.colYou}`}>
                          <p className={css.colLabel}>You manage</p>
                          <p className={css.colHead}>Your business and your client.</p>
                          <ul>
                            {YOU_MANAGE.map(t => (
                              <li key={t}>
                                <Check /> {t}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className={`${css.col} ${css.colGsx}`}>
                          <p className={css.colLabel}>GemstoneX manages</p>
                          <p className={css.colHead}>The sourcing infrastructure beneath it.</p>
                          <ul>
                            {GSX_MANAGES.map(t => (
                              <li key={t}>
                                <Check /> {t}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Verification */}
                  <section className={`${css.verify} ${css.section}`} id="verification">
                    <div className={css.wrap}>
                      <div className={css.verifyGrid}>
                        <div className={css.verifyCopy}>
                          <div className={css.eyebrow}>How verification works</div>
                          <div className={css.rule} />
                          <h2>Access is by application.</h2>
                          <p className={css.verifyLede}>
                            Submit your trade credentials and we run KYC to confirm you are a
                            professional buyer. Verified members gain access to certified inventory
                            and trade pricing. Applications are reviewed, so this is not instant
                            access.
                          </p>
                          <ul className={`${css.exampleProof} ${css.verifyProof}`}>
                            <li>
                              <Check /> Independent certificate
                            </li>
                            <li>
                              <Check /> Treatment recorded
                            </li>
                            <li>
                              <Check /> Origin documented
                            </li>
                          </ul>
                        </div>
                        <div className={css.verifyAside}>
                          <div className={css.example}>
                            <p className={css.exampleSpec} style={{ marginBottom: '18px' }}>
                              What a verified stone looks like.
                            </p>
                            <div className={css.exampleCard}>
                              <div className={css.exampleMedia}>
                                <img
                                  src={verifiedExample}
                                  alt="A de-identified certified emerald shown as a documentation example"
                                />
                                <span className={css.exampleLab}>
                                  <BadgeLab /> GRS
                                </span>
                                <span className={css.exampleVerify}>
                                  <BadgeVerify /> Verified
                                </span>
                              </div>
                              <div className={css.exampleBody}>
                                <p className={css.exampleSpec}>3.45ct · Oval · Vivid green</p>
                                <dl className={css.exampleRows}>
                                  <div className={css.exampleRow}>
                                    <dt>Treatment</dt>
                                    <dd>Insignificant (NCE)</dd>
                                  </div>
                                  <div className={css.exampleRow}>
                                    <dt>Certification</dt>
                                    <dd>GRS</dd>
                                  </div>
                                </dl>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Closing CTA band */}
                  <section className={css.cta} style={{ '--ctaImg': `url(${ctaBg})` }}>
                    <div className={css.wrap}>
                      <div className={`${css.eyebrow} ${css.ctaEyebrow}`}>Trade access</div>
                      <div className={`${css.rule} ${css.ruleCenter}`} />
                      <h2 className={css.ctaTitle}>The trade marketplace built on proof.</h2>
                      <NamedLink name="SignupPage" className={`${css.btn} ${css.btnPrimary}`}>
                        Apply for Trade Access <Arrow />
                      </NamedLink>
                      <p className={css.ctaMicro}>
                        Already a member?{' '}
                        <NamedLink name="LoginPage" className={css.ctaMicroLink}>
                          Log in
                        </NamedLink>
                        .
                      </p>
                    </div>
                  </section>
                </div>
              </Main>

              <Footer>
                <GemstoneSiteFooter showSupplier tagline="Your global sourcing extension — certified emeralds and coloured stones for the trade." />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export default TradeAccessPage;
